import { useSessionStorage } from '@mantine/hooks';
import { createContext, useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppContainerContextValue, ContentMeta, ContentPaneConfig, SiestaMode } from './types';

const AppContainerContext = createContext<AppContainerContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
  skipDefaultSidebar?: boolean;
  initialSidebarCollapsed?: boolean;
};

export function AppContainerProvider({
  children,
  skipDefaultSidebar = false,
  initialSidebarCollapsed = false,
}: Props) {
  const [sidebarContent, setSidebarContent] = useState<React.ReactNode | null>(null);
  const [contentMeta, setContentMeta] = useState<ContentMeta | null>(null);
  const [contentPaneConfig, setContentPaneConfig] = useState<ContentPaneConfig>({});
  const [skipSidebar, setSkipDefaultSidebar] = useState(skipDefaultSidebar);
  const [siestaMode, setSiestaMode] = useState<SiestaMode>('action');
  const [siestaBusyGlowTop, setSiestaBusyGlowTop] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState(() => uuidv4());
  const resetConversationId = () => {
    const newId = uuidv4();
    setConversationId(newId);
    return newId;
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useSessionStorage({
    key: 'app-sidebar-collapsed',
    defaultValue: initialSidebarCollapsed,
  });
  const [preventSidebarCollapse, setPreventSidebarCollapse] = useState(false);
  const [sidebarFullScreen, setSidebarFullScreen] = useSessionStorage({
    key: 'app-sidebar-fullscreen',
    defaultValue: false,
  });

  return (
    <AppContainerContext
      value={{
        skipDefaultSidebar: skipSidebar,
        setSkipDefaultSidebar,
        sidebarContent,
        setSidebarContent,
        contentMeta,
        setContentMeta,
        contentPaneConfig,
        setContentPaneConfig,
        siestaMode,
        setSiestaMode,
        siestaBusyGlowTop,
        setSiestaBusyGlowTop,
        conversationId,
        setConversationId,
        resetConversationId,
        sidebarCollapsed,
        setSidebarCollapsed,
        preventSidebarCollapse,
        setPreventSidebarCollapse,
        sidebarFullScreen,
        setSidebarFullScreen,
      }}
    >
      {children}
    </AppContainerContext>
  );
}

export function useAppContainerContext() {
  const context = useContext(AppContainerContext);
  if (context === undefined) {
    throw new Error('useAppContainerContext must be used within AppContainerProvider');
  }
  return context;
}
